/**
 * ShopManager — manages the persistent coin wallet, owned shop items,
 * item durability, and item category lifecycle.
 *
 * Three persistence categories:
 *   - Consumable: cleared on game over (Threia's Guide, Heart Potion)
 *   - Permanent: bought once, kept forever (Agmuth Pendant, Key Pendant)
 *   - Durability: persists across game overs but degrades with use;
 *                 when charges reach 0 the item breaks and can be repurchased
 *
 * Elemental Ring priority: when the Elemental Ring is active (owned with
 * charges > 0), individual element rings (Water/Lava/Poison) are dormant —
 * they don't consume charges and are hidden in HUD. If the Elemental Ring
 * breaks, individual rings reactivate automatically.
 *
 * Data shapes in localStorage:
 *   Wallet:     number (total persistent coins)
 *   Items:      { [itemId]: quantity }
 *   Durability: { [itemId]: currentCharges }
 */

import {
  SHOP_WALLET_KEY,
  SHOP_ITEMS_KEY,
  SHOP_DURABILITY_KEY,
} from '../config/game-config.js';

import {
  ShopItemType,
  ItemCategory,
  SHOP_ITEM_PRICES,
  SHOP_ITEM_MAX,
  SHOP_ITEM_CATEGORIES,
  SHOP_ITEM_DURABILITY,
} from '../utils/constants.js';

// ---------------------------------------------------------------
// Wallet persistence
// ---------------------------------------------------------------

/**
 * Get the current persistent coin balance.
 * @returns {number}
 */
export function getWallet() {
  try {
    const val = localStorage.getItem(SHOP_WALLET_KEY);
    if (val === null) return 0;
    const parsed = JSON.parse(val);
    return typeof parsed === 'number' && parsed >= 0 ? Math.floor(parsed) : 0;
  } catch {
    return 0;
  }
}

/**
 * Set the persistent coin balance (overwrites).
 * @param {number} amount
 */
export function setWallet(amount) {
  try {
    localStorage.setItem(SHOP_WALLET_KEY, JSON.stringify(Math.max(0, Math.floor(amount))));
  } catch (err) {
    console.warn('Failed to save wallet:', err);
  }
}

/**
 * Add coins to the persistent wallet.
 * @param {number} amount — coins to add
 * @returns {number} — new balance
 */
export function addToWallet(amount) {
  const balance = getWallet() + Math.max(0, Math.floor(amount));
  setWallet(balance);
  return balance;
}

/**
 * Deduct coins from the persistent wallet.
 * @param {number} amount — coins to deduct
 * @returns {boolean} — true if deduction succeeded (had enough), false otherwise
 */
export function deductFromWallet(amount) {
  const current = getWallet();
  const cost = Math.max(0, Math.floor(amount));
  if (current < cost) return false;
  setWallet(current - cost);
  return true;
}

// ---------------------------------------------------------------
// Item category helpers
// ---------------------------------------------------------------

/**
 * Get the persistence category for a shop item.
 * @param {string} itemId — one of ShopItemType values
 * @returns {string} — one of ItemCategory values
 */
export function getItemCategory(itemId) {
  return SHOP_ITEM_CATEGORIES[itemId] || ItemCategory.CONSUMABLE;
}

/**
 * Get the maximum durability (charges) for a durability-category item.
 * @param {string} itemId
 * @returns {number} — max charges, or 0 if not a durability item
 */
export function getMaxDurability(itemId) {
  return SHOP_ITEM_DURABILITY[itemId] || 0;
}

// ---------------------------------------------------------------
// Owned items persistence
// ---------------------------------------------------------------

/**
 * Get all owned shop items.
 * @returns {Object<string, number>} — map of itemId to quantity
 */
export function getOwnedItems() {
  try {
    const val = localStorage.getItem(SHOP_ITEMS_KEY);
    if (val === null) return {};
    const parsed = JSON.parse(val);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Save owned items to localStorage (overwrites).
 * @param {Object<string, number>} items
 */
export function setOwnedItems(items) {
  try {
    localStorage.setItem(SHOP_ITEMS_KEY, JSON.stringify(items));
  } catch (err) {
    console.warn('Failed to save shop items:', err);
  }
}

/**
 * Get the quantity of a specific owned item.
 * @param {string} itemId — one of ShopItemType values
 * @returns {number}
 */
export function getItemCount(itemId) {
  const items = getOwnedItems();
  return items[itemId] || 0;
}

/**
 * Check if the player owns a specific item (quantity > 0).
 * @param {string} itemId
 * @returns {boolean}
 */
export function hasItem(itemId) {
  return getItemCount(itemId) > 0;
}

// ---------------------------------------------------------------
// Durability persistence
// ---------------------------------------------------------------

/**
 * Get durability data for all items.
 * @returns {Object<string, number>} — map of itemId to current charges
 */
export function getDurabilityData() {
  try {
    const val = localStorage.getItem(SHOP_DURABILITY_KEY);
    if (val === null) return {};
    const parsed = JSON.parse(val);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Save durability data to localStorage.
 * @param {Object<string, number>} data
 */
export function setDurabilityData(data) {
  try {
    localStorage.setItem(SHOP_DURABILITY_KEY, JSON.stringify(data));
  } catch (err) {
    console.warn('Failed to save durability data:', err);
  }
}

/**
 * Get the current durability (remaining charges) for an item.
 * Returns 0 if the item has no durability entry or is not a durability item.
 * @param {string} itemId
 * @returns {number}
 */
export function getItemDurability(itemId) {
  if (getItemCategory(itemId) !== ItemCategory.DURABILITY) return 0;
  const data = getDurabilityData();
  return data[itemId] || 0;
}

/**
 * Initialize durability charges for an item (called on purchase).
 * @param {string} itemId
 */
function initDurability(itemId) {
  const maxCharges = getMaxDurability(itemId);
  if (maxCharges <= 0) return;
  const data = getDurabilityData();
  data[itemId] = maxCharges;
  setDurabilityData(data);
}

/**
 * Degrade an item by consuming charges.
 * If charges reach 0, the item breaks (removed from owned items and durability).
 *
 * @param {string} itemId
 * @param {number} [amount=1] — charges to consume
 * @returns {{ remaining: number, broken: boolean }} — result
 */
export function degradeItem(itemId, amount = 1) {
  if (getItemCategory(itemId) !== ItemCategory.DURABILITY) {
    return { remaining: 0, broken: false };
  }

  const data = getDurabilityData();
  const current = data[itemId] || 0;

  if (current <= 0) {
    return { remaining: 0, broken: false };
  }

  const newCharges = Math.max(0, current - Math.max(1, Math.floor(amount)));
  data[itemId] = newCharges;
  setDurabilityData(data);

  if (newCharges <= 0) {
    // Item breaks — remove from owned items and durability
    breakItem(itemId);
    return { remaining: 0, broken: true };
  }

  return { remaining: newCharges, broken: false };
}

/**
 * Break an item — remove it from owned items and durability storage.
 * @param {string} itemId
 */
export function breakItem(itemId) {
  // Remove from owned items
  const items = getOwnedItems();
  delete items[itemId];
  setOwnedItems(items);

  // Remove from durability data
  const data = getDurabilityData();
  delete data[itemId];
  setDurabilityData(data);
}

// ---------------------------------------------------------------
// Elemental Ring priority logic
// ---------------------------------------------------------------

/**
 * Check if the Elemental Ring is currently active (owned with charges > 0).
 * @returns {boolean}
 */
export function isElementalRingActive() {
  if (!hasItem(ShopItemType.ELEMENTAL_RING)) return false;
  return getItemDurability(ShopItemType.ELEMENTAL_RING) > 0;
}

/**
 * Individual element ring item IDs that are superseded by the Elemental Ring.
 */
const INDIVIDUAL_RING_IDS = Object.freeze([
  ShopItemType.WATER_RING,
  ShopItemType.LAVA_RING,
  ShopItemType.POISON_RING,
]);

/**
 * Check if a specific item is currently active/effective.
 *
 * This accounts for Elemental Ring priority: when the Elemental Ring is
 * active, individual rings are dormant (not active) even if owned.
 *
 * For durability items, "active" means owned AND has charges > 0.
 * For permanent items, "active" means owned.
 * For consumable items, "active" means owned (quantity > 0).
 *
 * @param {string} itemId
 * @returns {boolean}
 */
export function isItemActive(itemId) {
  if (!hasItem(itemId)) return false;

  const category = getItemCategory(itemId);

  // Durability items must have charges remaining
  if (category === ItemCategory.DURABILITY) {
    if (getItemDurability(itemId) <= 0) return false;
  }

  // Elemental Ring priority: individual rings are dormant when Elemental Ring is active
  if (INDIVIDUAL_RING_IDS.includes(itemId) && isElementalRingActive()) {
    return false;
  }

  return true;
}

/**
 * Check if an individual ring is suppressed by the Elemental Ring.
 * Used by the shop UI to show "Already protected" for individual rings.
 * @param {string} itemId
 * @returns {boolean}
 */
export function isRingSuppressedByElemental(itemId) {
  return INDIVIDUAL_RING_IDS.includes(itemId) && isElementalRingActive();
}

// ---------------------------------------------------------------
// Purchase logic
// ---------------------------------------------------------------

/**
 * Attempt to purchase a shop item. Deducts coins and adds the item.
 * For durability items, initializes charges on purchase.
 * For permanent items, blocks repurchase if already owned.
 *
 * @param {string} itemId — one of ShopItemType values
 * @returns {{ success: boolean, reason?: string }} — result of purchase attempt
 */
export function purchaseItem(itemId) {
  const price = SHOP_ITEM_PRICES[itemId];
  if (price === undefined) {
    return { success: false, reason: 'Unknown item' };
  }

  const category = getItemCategory(itemId);

  // Permanent items: cannot be repurchased once owned
  if (category === ItemCategory.PERMANENT && hasItem(itemId)) {
    return { success: false, reason: 'Already owned (permanent)' };
  }

  // Check max quantity
  const maxQty = SHOP_ITEM_MAX[itemId] || 1;
  const currentQty = getItemCount(itemId);

  if (currentQty >= maxQty) {
    return { success: false, reason: 'Already at max quantity' };
  }

  // Elemental Ring priority: block individual ring purchase when Elemental Ring is active
  if (INDIVIDUAL_RING_IDS.includes(itemId) && isElementalRingActive()) {
    return { success: false, reason: 'Already protected by Elemental Ring' };
  }

  const wallet = getWallet();
  if (wallet < price) {
    return { success: false, reason: 'Not enough coins' };
  }

  // Deduct coins
  setWallet(wallet - price);

  // Add item
  const items = getOwnedItems();
  items[itemId] = (items[itemId] || 0) + 1;
  setOwnedItems(items);

  // Initialize durability if applicable
  if (category === ItemCategory.DURABILITY) {
    initDurability(itemId);
  }

  return { success: true };
}

/**
 * Consume one unit of a consumable item (e.g., Heart Potion used).
 * Only works for consumable items.
 * @param {string} itemId
 * @returns {boolean} — true if consumed, false if none owned
 */
export function consumeItem(itemId) {
  const items = getOwnedItems();
  const qty = items[itemId] || 0;
  if (qty <= 0) return false;

  items[itemId] = qty - 1;
  if (items[itemId] <= 0) {
    delete items[itemId];
  }
  setOwnedItems(items);
  return true;
}

// ---------------------------------------------------------------
// Game over cleanup
// ---------------------------------------------------------------

/**
 * Clear ONLY consumable shop items (called on game over).
 * Permanent items and durability items (with remaining charges) survive.
 * Does NOT clear the wallet or durability data.
 */
export function clearConsumableItems() {
  try {
    const items = getOwnedItems();
    let changed = false;

    for (const itemId of Object.keys(items)) {
      if (getItemCategory(itemId) === ItemCategory.CONSUMABLE) {
        delete items[itemId];
        changed = true;
      }
    }

    if (changed) {
      setOwnedItems(items);
    }
  } catch (err) {
    console.warn('Failed to clear consumable items:', err);
  }
}

/**
 * Clear ALL owned shop items and durability data (full reset).
 * Does NOT clear the wallet.
 */
export function clearAllItems() {
  try {
    localStorage.removeItem(SHOP_ITEMS_KEY);
    localStorage.removeItem(SHOP_DURABILITY_KEY);
  } catch (err) {
    console.warn('Failed to clear shop items:', err);
  }
}

// ---------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------

/**
 * Check if the player has any active shop items at all.
 * @returns {boolean}
 */
export function hasAnyItems() {
  const items = getOwnedItems();
  return Object.keys(items).some(itemId => {
    const qty = items[itemId] || 0;
    if (qty <= 0) return false;
    // For durability items, also check charges
    if (getItemCategory(itemId) === ItemCategory.DURABILITY) {
      return getItemDurability(itemId) > 0;
    }
    return true;
  });
}

/**
 * Get a summary of all active shop items for HUD display.
 * Implements Elemental Ring priority: when Elemental Ring is active,
 * individual element rings are hidden from the list.
 *
 * @returns {Array<{ id: string, count: number, durability?: number, maxDurability?: number }>}
 */
export function getActiveItemsList() {
  const items = getOwnedItems();
  const result = [];
  const elementalActive = isElementalRingActive();

  // Iterate in a fixed order for consistent HUD display
  const order = [
    ShopItemType.AGMUTH_PENDANT,
    ShopItemType.ELEMENTAL_RING,
    ShopItemType.HAMMER_OF_THREIA,
    ShopItemType.HEART_PENDANT,
    ShopItemType.HEART_POTION,
    ShopItemType.KEY_PENDANT,
    ShopItemType.LAVA_RING,
    ShopItemType.POISON_RING,
    ShopItemType.TEAR_OF_THREIA,
    ShopItemType.THREIAS_GUIDE,
    ShopItemType.TOTEM_VORACIOUS,
    ShopItemType.WATER_RING,
  ];

  for (const id of order) {
    const count = items[id] || 0;
    if (count <= 0) continue;

    const category = getItemCategory(id);

    // Durability items: skip if no charges remain
    if (category === ItemCategory.DURABILITY) {
      const charges = getItemDurability(id);
      if (charges <= 0) continue;
    }

    // Elemental Ring priority: hide individual rings when Elemental Ring is active
    if (elementalActive && INDIVIDUAL_RING_IDS.includes(id)) {
      continue;
    }

    const entry = { id, count };

    // Attach durability info for durability items
    if (category === ItemCategory.DURABILITY) {
      entry.durability = getItemDurability(id);
      entry.maxDurability = getMaxDurability(id);
    }

    result.push(entry);
  }

  return result;
}

/**
 * Check if a specific item can currently be purchased.
 * Considers category rules, Elemental Ring priority, quantity limits, and wallet.
 *
 * @param {string} itemId
 * @returns {{ canBuy: boolean, reason?: string }}
 */
export function canPurchaseItem(itemId) {
  const price = SHOP_ITEM_PRICES[itemId];
  if (price === undefined) {
    return { canBuy: false, reason: 'Unknown item' };
  }

  const category = getItemCategory(itemId);

  // Permanent items: cannot repurchase once owned
  if (category === ItemCategory.PERMANENT && hasItem(itemId)) {
    return { canBuy: false, reason: 'Owned (permanent)' };
  }

  // Elemental Ring priority: block individual rings when Elemental Ring is active
  if (INDIVIDUAL_RING_IDS.includes(itemId) && isElementalRingActive()) {
    return { canBuy: false, reason: 'Already protected' };
  }

  // Max quantity check
  const maxQty = SHOP_ITEM_MAX[itemId] || 1;
  const currentQty = getItemCount(itemId);
  if (currentQty >= maxQty) {
    return { canBuy: false, reason: 'Max owned' };
  }

  // Wallet check
  const wallet = getWallet();
  if (wallet < price) {
    return { canBuy: false, reason: 'Not enough coins' };
  }

  return { canBuy: true };
}
